import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { useWholesaleSettings, useSaveWholesaleSettings } from "@/hooks/use-wholesale";
import { parseNumberInput } from "@/lib/wholesale-format";
import { IssuerForm } from "@/components/wholesale/issuer-form";

/** Radix Select ne prima prazan string kao vrijednost stavke. */
const ALL = "__all__";

export function WholesaleSettingsPage() {
  const { data, isLoading, error } = useWholesaleSettings();
  const save = useSaveWholesaleSettings();
  const [enabled, setEnabled] = useState<string[]>([]);
  const [vatRate, setVatRate] = useState("");
  const [defaultStore, setDefaultStore] = useState(ALL);
  const [dirty, setDirty] = useState(false);

  // Postavke sa servera pune formu samo dok korisnik nije ništa dirao, da
  // invalidacija nakon spremanja ne pregazi unos u toku.
  useEffect(() => {
    if (!data || dirty) return;
    setEnabled(data.sources.map((s) => s.id));
    setVatRate(String(data.vatRate));
    setDefaultStore(data.defaultStoreId || ALL);
  }, [data, dirty]);

  function toggle(id: string, on: boolean) {
    setDirty(true);
    setEnabled((ids) => (on ? [...new Set([...ids, id])] : ids.filter((x) => x !== id)));
    // Isključen shop ne može ostati zadani — server bi odbio spremanje.
    if (!on && defaultStore === id) setDefaultStore(ALL);
  }

  async function handleSave() {
    const rate = parseNumberInput(vatRate);
    if (rate === null || rate < 0 || rate > 100) {
      toast.error("Stopa PDV-a mora biti između 0 i 100");
      return;
    }
    try {
      await save.mutateAsync({
        vatRate: rate,
        sourceStoreIds: enabled,
        defaultStoreId: defaultStore === ALL ? "" : defaultStore,
      });
      setDirty(false);
      toast.success("Postavke spremljene");
    } catch (e: any) {
      toast.error(e?.message || "Greška pri spremanju postavki");
    }
  }

  return (
    <div className="p-4 flex flex-col gap-3 max-w-2xl">
      <h2 className="text-sm font-medium">Postavke veleprodaje</h2>

      <IssuerForm />

      <div className="h-px bg-foreground/10" />

      {error && <p className="text-destructive text-xs">{(error as Error).message}</p>}

      {isLoading ? (
        <div className="flex flex-col gap-2">
          {Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-10 w-full" />)}
        </div>
      ) : !data ? (
        <p className="text-xs text-muted-foreground">Postavke nisu dostupne.</p>
      ) : (
        <>
          <div className="flex flex-col gap-2">
            <p className="text-[10px] uppercase tracking-[0.08em] text-muted-foreground">Shopovi u veleprodaji</p>
            {data.candidates.length === 0 ? (
              <p className="text-xs text-muted-foreground">Nema WooCommerce shopova.</p>
            ) : (
              <div className="ring-1 ring-foreground/10 divide-y">
                {data.candidates.map((c) => {
                  const webhook = c.syncMode === "webhook";
                  const isOn = enabled.includes(c.id);
                  // Shop koji nije u webhook modu se ne može uključiti, ali ako je već
                  // uključen mora ostati isključiv da se stanje može ispraviti.
                  const locked = !webhook && !isOn;
                  const row = (
                    <div className="flex items-center justify-between gap-3 p-2.5 text-xs">
                      <div className="flex flex-col min-w-0">
                        <span className="font-medium truncate">{c.name}</span>
                        <span className="text-muted-foreground">{webhook ? "Webhook sinhronizacija" : "Nije u webhook modu"}</span>
                      </div>
                      <Switch checked={isOn} disabled={locked} onCheckedChange={(v) => toggle(c.id, v)} />
                    </div>
                  );
                  return locked ? (
                    <TooltipProvider key={c.id}>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <div>{row}</div>
                        </TooltipTrigger>
                        <TooltipContent>Shop nije u webhook modu</TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  ) : (
                    <div key={c.id}>{row}</div>
                  );
                })}
              </div>
            )}
          </div>

          <div className="flex flex-col gap-1 max-w-64">
            <Label className="text-[10px] uppercase tracking-[0.08em] text-muted-foreground">Zadani shop</Label>
            <Select value={defaultStore} onValueChange={(v) => { setDirty(true); setDefaultStore(v); }}>
              <SelectTrigger size="sm" className="h-8 text-xs">
                <SelectValue placeholder="Svi shopovi" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>Svi shopovi</SelectItem>
                {data.candidates.filter((c) => enabled.includes(c.id)).map((c) => (
                  <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-[11px] text-muted-foreground">Unaprijed odabran u katalogu, izlogu i pretrazi artikala.</p>
          </div>

          <div className="flex flex-col gap-1 max-w-40">
            <Label className="text-[10px] uppercase tracking-[0.08em] text-muted-foreground">Stopa PDV-a (%)</Label>
            <Input
              type="number" min={0} max={100} step="0.01"
              className="h-8 text-xs tabular-nums"
              value={vatRate}
              onChange={(e) => { setDirty(true); setVatRate(e.target.value); }}
            />
          </div>

          <div>
            <Button size="sm" onClick={handleSave} disabled={save.isPending}>
              {save.isPending ? "Spremanje…" : "Sačuvaj"}
            </Button>
          </div>
        </>
      )}
    </div>
  );
}
